import { useMemo, useContext, useRef, useEffect, useCallback } from "react";

import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Card from "react-bootstrap/Card";

import { Link, useNavigate } from "react-router-dom";

import {
  ColumnDef
} from "@tanstack/react-table";

import { 
  formatRelative,
  fromUnixTime,
  getUnixTime,
  sub
} from "date-fns";

import {
  useQuery
} from "@apollo/client";

import { 
  Alert, 
  Table, 
  WidgetCard,
  StatusBadge,
  Skeleton,
  Button
} from "@evos/components";

import Header from "components/Header";
// import RuntimeTypeCheck from "components/RuntimeTypeCheck";
import NotificationsList from "components/NotificationsList";
import FirmwareChart from "./components/FirmwareChart";
import StatusChart from "./components/StatusChart";

import { useWindowSize } from "@evos/hooks";
import { AuthContext } from "lib/context/auth";
import { lastCommsFilterToTimestamp } from "lib/functions/charger";

import { 
  CHARGERS,
  CHARGER_STATUS_MESSAGES,
  FIRMWARE_VERSIONS,
  STATUSES,
  SITES_COUNT,
  VIDS_COUNT
} from "./gql";

import "./styles/hardwareDashboard.css";

import type ReactEChartsCore from "echarts-for-react";
import type { ECharts } from "echarts";
import type{ 
  ChargersConnection, 
  ChargerMonitors,
  SitesCount,
  VidsCount
} from "types";

function useAuth() {
  return useContext(AuthContext);
}

interface TableData  {
  serial: string;
  lastWireguardHeartbeat: string;
  allowedIps: string;
  location: string;
  mode: string;
  salesforceStatus: string;
  ocppStatus: "Available" | "Preparing" | "Charging" | "SuspendedEVSE" | "SuspendedEV" | "Finishing" | "Unavailable" | "Faulted" | "Unknown";
  ocppServer: string;
  firmwareVersion: string;
}

type DashboardStatusFrequencies = Partial<Record<
  "Available" | "Charging" | "Faulted" | "Finishing" | "Offline" | "Preparing" | "SuspendedEV" | "SuspendedEVSE" | "Unavailable",
  number
>>;

function chartDataFromStatusFrequencies(
  frequencies: DashboardStatusFrequencies | null | undefined,
  colorForStatus: (status: string) => string
) {
  return Object.entries(frequencies ?? {})
    .filter((entry): entry is [string, number] => entry[0] !== "__typename" && typeof entry[1] === "number")
    .map(([name, value]) => ({
      value,
      name,
      itemStyle: { color: colorForStatus(name) }
    }));
}

export default function HardwareDashboard() {
  const auth = useAuth();
  const darkMode = auth?.darkMode ?? false;
  const windowWidth = useWindowSize().width ?? window.innerWidth;
  const navigate = useNavigate();

  const twentyFourHoursAgoTimestamp = useMemo(() => (sub(new Date(), { hours: 24 }).toISOString()), []);

  const { 
    data: chargerErrorData, 
    loading: chargerErrorLoading, 
    error: chargerErrorDataError,
    refetch: refetchErrors
  } = useQuery<ChargerMonitors>(
    CHARGER_STATUS_MESSAGES,
    { 
      variables: {
        n: 100,
        distinct_by: ["serial"],
        where: {
          status: {
            in: ["Faulted"]
          },
          receiveTimestamp: {
            gte: twentyFourHoursAgoTimestamp
          }
        }
      },
      fetchPolicy: "cache-and-network",
      pollInterval: 120000,
      onCompleted: () => {
        if (chargersData != null) {
          refetch();
        }
      }
    }
  );

  const { 
    data: statusData, 
    loading: statusLoading, 
    error: statusError
  } = useQuery<ChargerMonitors>(
    STATUSES,
    { 
      fetchPolicy: "cache-and-network",
      pollInterval: 10000,
    }
  );

  const fortyEightHoursAgoTimestamp = useMemo(() => (sub(new Date(), { hours: 48 }).toISOString()), []);

  const {
    data: firmwareVersionData,
    loading: firmwareVersionLoading,
    error: firmwareVersionError
  } = useQuery<ChargersConnection>(
    FIRMWARE_VERSIONS, 
    {
      variables: {
        n: 10000,
        after: null,
        where: {
          last_comms: {
            gte: fortyEightHoursAgoTimestamp
          }
        }
      },
      pollInterval: 120000,
    }
  );

  const { 
    data: chargersData, 
    loading: chargersLoading, 
    error: chargersError, 
    refetch 
  } = useQuery<ChargersConnection>(
    CHARGERS,
    { 
      variables: {
        chargers_n: 100, 
        after: null,
        errors_n: 1,
        chargers_where: { 
          serial: {
            in: [...new Set(
              chargerErrorData?.charger_monitors?.charger_status_messages?.edges.map((e) => e.node.serial)
            )]
          }
        }
      },
      fetchPolicy: "cache-and-network",
      notifyOnNetworkStatusChange: true,
      skip: (chargerErrorData?.charger_monitors?.charger_status_messages?.edges?.length ?? 0) === 0,
    }
  );

  const {
    data: siteCountData,
    loading: siteCountLoading,
  } = useQuery<SitesCount>(
    SITES_COUNT,
    {
      fetchPolicy: "cache-and-network",
      variables: {
        n: 1
      }
    }
  );

  const {
    data: vidCountData,
    loading: vidCountLoading,
  } = useQuery<VidsCount>(
    VIDS_COUNT,
    {
      fetchPolicy: "cache-and-network",
      variables: {
        n: 1
      }
    }
  );

  const recentErrors = (chargerErrorData != null)
    ? chargerErrorData.charger_monitors?.charger_status_messages?.edges.map((e) => e.node)
    : [];

  const pageInfoObject = chargersData?.chargers?.pageInfo;

  const chargerTableData = chargersData?.chargers?.edges.map((d) => ({
    serial: d?.node?.serial,
    lastComms: (d?.node?.salesforce_details?.last_heartbeat != null && d?.node?.salesforce_details?.last_status_notification != null) 
      ? formatRelative(
        fromUnixTime(
          Math.max(
            getUnixTime(new Date(d.node.salesforce_details.last_heartbeat)), 
            getUnixTime(new Date(d.node.salesforce_details.last_status_notification)) 
          )
        ), Date.now()
      ) 
      : "Never",
    allowedIps: (d?.node?.salesforce_details as { vpn_ip?: string } | undefined)?.vpn_ip != null &&
      (d?.node?.salesforce_details as { vpn_ip?: string }).vpn_ip !== ""
      ? (d?.node?.salesforce_details as { vpn_ip?: string }).vpn_ip
      : (d?.node?.vpn_status?.connections[0]?.allowed_ips ?? "None"),
    ocppStatus: d?.node?.salesforce_details?.ocpp_status ?? "Unknown",
    ocppServer: d?.node?.salesforce_details?.ocpp_server ?? "Unknown",
    firmwareVersion: d?.node?.salesforce_details?.firmware_version ?? "Unknown",
  }));

  const statusColors = (status: string) => {
    switch(status) {
    case "Available":
      return "#D1C2FF";
    case "Preparing": case "SuspendedEVSE":
      return "#3CC13B";
    case "Charging": case "SuspendedEV":
      return "#3E66FB";
    case "Finishing":
      return "#FF9A00";
    case "Faulted": case "Unavailable":
      return "#C92E2E";
    case "Offline": case "Unknown":
      return "#696969";
    default:
      return "#3E66FB";
    }
  };

  const chargerColumns = useMemo<Array<ColumnDef<TableData>>>(
    () => [
      {
        header: "Serial",
        accessorKey: "serial",
        cell: cell => <Link to={`/c/${cell.row.original.serial}`} style={{color: darkMode ? "#1E90FF" : "blue"}}>{cell.row.original.serial}</Link>
      },
      {
        header: "Last Comms",
        accessorKey: "lastComms"
      },
      {
        header: "IP Address",
        accessorKey: "allowedIps",
        cell: cell => cell.row.original.allowedIps?.includes("/")
          ? cell.row.original.allowedIps?.split("/")?.[0]
          : cell.row.original.allowedIps
      },
      {
        header: "Current OCPP Status",
        accessorKey: "ocppStatus",
        cell: cell => <><StatusBadge status={cell.row.original.ocppStatus} showStandardName /> {cell.row.original.ocppStatus}</>
      },
      {
        header: "OCPP Server",
        accessorKey: "ocppServer",
        cell: cell => cell.row.original.ocppServer?.includes("ws")
          ? cell.row.original.ocppServer?.split("ws://")[1]
          : cell.row.original.ocppServer
      },
      {
        header: "Firmware Version",
        accessorKey: "firmwareVersion"
      },
      {
        header: "Last Status Message",
        accessorKey: "lastStatusMessage"
      }
    ],
    [darkMode]
  );

  const statusChartRef = useRef<ReactEChartsCore | null>(null);
  const firmwareChartRef = useRef<ReactEChartsCore | null>(null);

  const statusChartData = useMemo(() => (
    chartDataFromStatusFrequencies(
      statusData?.charger_monitors?.status?.dashboard_ocpp_status_frequencies,
      statusColors
    )
  ), [statusData?.charger_monitors?.status?.dashboard_ocpp_status_frequencies]);

  const totalChargerStatuses = statusChartData.reduce((acc, entry) => acc + entry.value, 0);
  const offlineChargerCount = statusChartData.find((entry) => entry.name === "Offline")?.value ?? 0;

  const firmwareChartData = useMemo(() => {
    const firmwareFrequencies = (!firmwareVersionLoading && (firmwareVersionData?.chargers?.edges != null))
      ? firmwareVersionData.chargers.edges
        .map((e) => e.node.salesforce_details?.firmware_version ?? "Unknown")
        .reduce((acc, value) => {
          acc[value] = (acc[value] || 0) + 1;
          return acc;
        }, {} as { [key: string]: number })
      : {};
    return Object.entries(firmwareFrequencies)
      .map(([name, value]) => {
        return {
          value,
          name,
          ...(name === "Unknown" && { itemStyle: { color: "#696969" } })
        };
      });
  }, [firmwareVersionData?.chargers?.edges, firmwareVersionLoading]);

  const handleFirmwareSliceClick = useCallback((name: string) => {
    if (name === "Unknown") {
      return;
    }

    const search = new URLSearchParams({
      firmware_version: name,
      last_comms: lastCommsFilterToTimestamp("< 10 mins")
    }).toString();
    navigate(`/charger-list?${search}`);
  }, [navigate]);

  useEffect(() => {
    const statusChartInstance: ECharts | undefined = statusChartRef?.current?.getEchartsInstance();

    if (statusChartInstance != null && !statusLoading) {
      statusChartInstance.on("click", (params) => {
        if (params.name !== "Offline") {
          navigate(`/charger-list?ocpp_status=${params.name}`);
        }
      });
    }

    return () => {
      statusChartInstance?.off("click");
    };
  }, [navigate, statusChartRef, statusLoading]);

  
  return (
    <div id="hardware-dashboard">
      <Header />
      <Container>
        <Row className="mt-3 pb-5">
          <Col xs={12}>
            <Card className={`pb-3 mt-4 ${darkMode ? "bg-dark" : ""}`}>
              <Row className="align-items-center mt-3 mx-2">
                <Col>
                  <h3 className="mb-0" style={{fontSize: "1.5em"}}>Faulted Chargers</h3>
                </Col>
                <Col className="d-flex justify-content-end">
                  <Button 
                    variant="outline-secondary" 
                    className="d-flex align-items-center ms-3" 
                    onClick={() => refetchErrors()} 
                    isLoading={chargersLoading}
                    loadingText="Reloading..."
                  >
                    Reload
                  </Button>
                  <Link to="/charger-list" onClick={() => refetch()}><Button className="ms-3">View all chargers</Button></Link>
                </Col>
              </Row>
              <Table
                className="mt-0 mb-0"
                error={chargersError}
                isLoading={chargersLoading || chargerErrorLoading}
                columns={chargerColumns}
                darkMode={darkMode!}
                data={chargerTableData ?? []}
                noDataText="No chargers faulted in last 24 hours"
                pageInfo={pageInfoObject!}
                totalCount={chargersData?.chargers?.totalCount}
              />
            </Card>
          </Col>
          <Col xs={12}>
            <Row className="d-flex justify-content-center">
              <Col xs={12} md={6}>
                <Card className={`p-4 mt-4 table-responsive ${darkMode ? "bg-dark" : ""}`}>
                  <Row>
                    <Col xs={6}>
                      <Row>
                        <Col xs={12}>
                          <p className={`mb-1 ${darkMode ? "text-light" : "text-secondary"}`}>CHARGERS</p>
                        </Col>
                        <Col xs={12}>
                          { !chargersLoading
                            ? <h4>{totalChargerStatuses}</h4>
                            : <Skeleton height="30px" width="40%" />
                          }
                        </Col>
                      </Row>
                    </Col>
                    <Col xs={6}>
                      <Row>
                        <Col xs={12}>
                          <p className={`mb-1 ${darkMode ? "text-light" : "text-secondary"}`}>OFFLINE</p>
                        </Col>
                        <Col xs={12}>
                          { !statusLoading
                            ? <h4 className="text-danger">{offlineChargerCount}</h4>
                            : <Skeleton height="30px" width="40%" />
                          }
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                </Card>
              </Col>
              <Col xs={12} md={6}>
                <Card className={`p-4 mt-4 ${darkMode ? "bg-dark" : ""}`}>
                  <Row>
                    <Col xs={6}>
                      <Row>
                        <Col xs={12}>
                          <p className={`mb-1 ${darkMode ? "text-light" : "text-secondary"}`}>VIDS</p>
                        </Col>
                        <Col xs={12}>
                          { !vidCountLoading
                            ? <h4>{vidCountData?.vehicles?.totalCount ?? 0}</h4>
                            : <Skeleton height="30px" width="40%" /> 
                          }
                        </Col>
                      </Row>
                    </Col>
                    <Col xs={6}>
                      <Row>
                        <Col xs={12}>
                          <p className={`mb-1 ${darkMode ? "text-light" : "text-secondary"}`}>SITES</p>
                        </Col>
                        <Col xs={12}>
                          { !siteCountLoading
                            ? <h4>{siteCountData?.sites?.totalCount ?? 0}</h4>
                            : <Skeleton height="30px" width="40%" />
                          }
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                </Card>
              </Col>
              {/* <Col xs={12} md={6} xl={3}>
                <Card className={`p-4 mt-4 ${darkMode ? "bg-dark" : ""}`}>
                  <Row>
                    <Col xs={6} style={{borderRight: `1px solid ${darkMode ? "white" : "grey"}`}}>
                      <Row>
                        <Col xs={12}>
                          <p className={`mb-1 ${darkMode ? "text-light" : "text-secondary"}`}>MTBF</p>
                        </Col>
                        <Col xs={12}>
                          <h4 className="text-danger">d h m</h4>
                        </Col>
                      </Row>
                    </Col>
                    <Col xs={6}>
                      <Row className="ps-2">
                        <Col xs={12}>
                          <p className={`mb-1 ${darkMode ? "text-light" : "text-secondary"}`}>MTTR</p>
                        </Col>
                        <Col xs={12}>
                          <h4 className="text-danger">h m</h4>
                        </Col>
                      </Row>
                    </Col>
                  </Row>
                </Card>
              </Col> */}
              {/* <Col xs={12} md={6} xl={3}>
                <Card className={`p-4 mt-4 ${darkMode ? "bg-dark" : ""}`}>
                  <Row>
                    <Col xs={6} style={{borderRight: `1px solid ${darkMode ? "white" : "grey"}`}}>
                      <Row>
                        <Col xs={12}>
                          <p className={`mb-1 ${darkMode ? "text-light" : "text-secondary"}`}>UPTIME</p>
                        </Col>
                        <Col xs={12}>
                          <h4 className="text-success">%</h4>
                        </Col>
                      </Row>
                    </Col>
                    <Col xs={6}>
                      <UptimeChart
                        uptimeValue={0.9}
                        loading={false}
                        error={chargersError}
                      />
                    </Col>
                  </Row>
                </Card>
              </Col> */}
            </Row>
          </Col>
          <Col xs={12}>
            <Row className="d-flex justify-content-center mt-4">
              <Col xs={12} lg={4} className="pb-4 pb-lg-0">
                <WidgetCard 
                  darkMode={darkMode}
                  titleText="Charger Status"
                  style={{height: "100%"}}
                  cardBodyClassname={ (statusError != null || statusLoading)
                    ? "d-flex align-items-center justify-content-center" 
                    : ""
                  }
                >
                  { statusError == null
                    ? <Row className="align-items-center">
                      <Col className="p-4 d-flex">
                        { statusLoading
                          ? <Skeleton
                            height="250px"
                            width="250px"
                            innerClassName="rounded-circle"
                          />
                          : <StatusChart
                            darkMode={darkMode!}
                            total={totalChargerStatuses}
                            chartData={statusChartData}
                            enableHorizontalLegend={windowWidth < 445 || (windowWidth > 767 && windowWidth < 1300)}
                            assetName="Chargers"
                            chartRef={statusChartRef}
                          />
                        }
                      </Col>
                    </Row>
                    : <Row>
                      <Col className="d-flex align-items-center justify-content-center">
                        <Alert variant="danger" showIcon message="There was an error getting charger status data. Please try again" />
                      </Col>
                    </Row>
                  }
                </WidgetCard>
              </Col>
              <Col xs={12} lg={4} className="pb-4 pb-lg-0">
                <WidgetCard 
                  darkMode={darkMode}
                  titleText="Faulted Status Messages"
                  style={{height: "480px", overflow: "auto", marginBottom: 0}}
                  cardBodyClassname={`${(chargerErrorDataError != null)
                    ? "d-flex align-items-center justify-content-center" 
                    : "p-0"}
                  `}
                > { chargerErrorDataError == null
                    ? <NotificationsList
                      errorData={recentErrors?.slice(0, 10)}
                      loading={chargerErrorLoading}
                      darkMode={darkMode!}
                      emptyStateMessage="No faulted status messages in the last 24 hours"
                    />
                    : <Row>
                      <Col className="d-flex align-items-center justify-content-center">
                        <Alert variant="danger" showIcon message="There was an error getting charger status message data. Please try again" />
                      </Col>
                    </Row>
                  }
                </WidgetCard>
              </Col>
              <Col xs={12} lg={4} className="pb-4 pb-lg-0">
                <WidgetCard 
                  darkMode={darkMode}
                  titleText="Firmware Version"
                  titleElement={<div className="small text-muted">Last Comms &lt;48hrs</div>}
                  style={{height: "100%"}}
                  cardBodyClassname={ (firmwareVersionError != null || firmwareVersionLoading)
                    ? "d-flex align-items-center justify-content-center" 
                    : ""
                  }
                >
                  { firmwareVersionError == null
                    ? <Row className="align-items-center">
                      <Col className="p-4 d-flex">
                        { firmwareVersionLoading
                          ? <Skeleton
                            height="250px"
                            width="250px"
                            innerClassName="rounded-circle"
                          />
                          : <FirmwareChart
                            chartData={firmwareChartData}
                            chartRef={firmwareChartRef}
                            onSliceClick={handleFirmwareSliceClick}
                          />
                        }
                      </Col>
                    </Row>
                    : <Row>
                      <Col className="d-flex align-items-center justify-content-center">
                        <Alert variant="danger" showIcon message="There was an error getting charger status data. Please try again" />
                      </Col>
                    </Row>
                  }
                </WidgetCard>
              </Col>
            </Row>
          </Col>
        </Row>
      </Container>
    </div>
  );
}
